interface StytchError {
  error_type?: string;
  error_message?: string;
  status_code?: number;
}

export function getStytchErrorMessage(error: unknown): string {
  const stytchError = error as StytchError;

  // Handle specific Stytch error types
  switch (stytchError.error_type) {
    // Authentication errors
    case "invalid_email":
      return "Please enter a valid email address";
    case "email_not_found":
      return "If an account exists with this email, you will receive a reset link";
    case "invalid_password":
      return "Invalid username or password";
    case "weak_password":
      return "Password doesn't meet our strength requirements";
    case "breached_password":
      return "This password has been found in a data breach. Please choose a different password";
    case "reset_password":
      return "Your password has expired, please reset it and try again";
    case "no_user_password":
      return "No user password found. Please contact your supervisor";

    // Rate limiting
    case "rate_limit_exceeded":
      return "Too many requests - please wait before trying again";

    // Email validation
    case "domain_not_allowed":
      return "Email domain not allowed";
    case "disposable_email_not_allowed":
      return "Disposable email addresses are not allowed";
    case "duplicate_email":
      return "An account with this email already exists";

    // Phone number errors
    case "invalid_phone_number":
      return "Please enter a valid phone number";
    case "duplicate_phone_number":
      return "An account with this phone number already exists";

    // OTP errors
    case "invalid_token":
      return "Invalid or expired verification code";
    case "expired_token":
      return "Verification code has expired - please request a new one";
    case "token_already_used":
      return "This verification code has already been used";

    // Session errors
    case "session_not_found":
      return "Session not found - please log in again";
    case "session_expired":
      return "Your session has expired - please log in again";

    // TOTP errors
    case "active_totp_exists":
      return "You already have an active authenticator app configured";
    case "invalid_totp_code":
      return "Invalid authenticator code";

    // WebAuthn errors
    case "webauthn_not_supported":
      return "WebAuthn is not supported on this device";
    case "webauthn_registration_failed":
      return "Failed to register security key";

    // Project configuration errors
    case "project_not_found":
      return "Project configuration error - please contact support";
    case "project_disabled":
      return "Service temporarily unavailable - please contact support";

    // Technical errors
    case "ad_blocker_detected":
      return "Request blocked by ad blocker - please disable and try again";
    case "captcha_required":
      return "Please complete the captcha verification";
    case "forbidden_ip":
      return "Access denied from this location";

    // Network/stack errors
    case "wrong_stack_live":
      return "Configuration error - please contact support";
    case "wrong_stack_test":
      return "Configuration error - please contact support";

    default:
      if (stytchError.error_message) {
        return stytchError.error_message;
      }
      switch (stytchError.status_code) {
        case 400:
          return "Invalid request - please check your input";
        case 401:
          return "Invalid username or password";
        case 403:
          return "Access denied";
        case 404:
          return "Resource not found";
        case 405:
          return "Method not allowed";
        case 429:
          return "Too many failed attempts - please wait 10 minutes and try again later";
        case 499:
          return "Request cancelled";
        case 500:
          return "Server error - please try again later";
        case 503:
          return "Service temporarily unavailable - please try again later";
        default:
          return "Something went wrong - please try again";
      }
  }
}

export function getAuthenticationErrorMessage(error: unknown): string {
  const stytchError = error as StytchError;

  // Specific handling for authentication flows
  if (stytchError.status_code === 401) {
    return "Invalid username or password";
  }

  if (stytchError.status_code === 429) {
    return "Too many failed attempts - please wait 10 minutes and try again later";
  }

  return getStytchErrorMessage(error);
}

export function getPasswordResetErrorMessage(error: unknown): string {
  const stytchError = error as StytchError;

  // Specific handling for password reset flows
  switch (stytchError.error_type) {
    case "email_not_found":
      // Security: don't reveal if email exists
      return "If an account exists with this email, you will receive a reset link";
    case "invalid_token":
      return "Invalid or expired reset link - please request a new one";
    case "expired_token":
      return "Reset link has expired - please request a new one";
    default:
      return getStytchErrorMessage(error);
  }
}
